/**
 * Testi italiani dell'elenco degli utenti della piattaforma.
 *
 * `forbidden` non è un messaggio d'errore: la lettura è riservata agli
 * amministratori, la voce di menu resta visibile a tutti e il 403 atteso si
 * spiega invece di essere reso come un guasto.
 */

/** Testi dell'area utenti. */
export const users = {
  /** Titolo della pagina. */
  title: 'Utenti',
  /** Frase che dice a che cosa serve la pagina. */
  description:
    "Gli utenti conosciuti dalla piattaforma. L'elenco è di sola lettura: account e password si gestiscono altrove.",
  /** Colonne della tabella. */
  columns: {
    /** Nome con cui l'utente accede. */
    username: 'Nome utente',
    /** Nome e cognome, quando ci sono. */
    displayName: 'Nome',
    /** Indirizzo di posta. */
    email: 'Email',
    /** Ruoli globali. */
    roles: 'Ruoli',
    /** Account attivo o disabilitato. */
    status: 'Stato',
    /** Piano dell'utente (ADR 0023). */
    plan: 'Piano',
    /** Comandi della riga (oggi solo «Accedi come…»). */
    actions: 'Azioni',
  },
  /** Account attivo. */
  enabled: 'Attivo',
  /** Account disabilitato. */
  disabled: 'Disabilitato',
  /** Nessun utente nella directory. */
  empty: 'Nessun utente registrato.',
  /** Lettura riservata agli amministratori della piattaforma. */
  forbidden: "Per vedere l'elenco degli utenti serve il ruolo di amministratore della piattaforma.",
  /** Lettura non riuscita. */
  loadFailed: 'Caricamento degli utenti non riuscito. Riprova.',

  /** Piano per utente (ADR 0023). Lo cambia solo un amministratore della piattaforma. */
  plan: {
    /** Nomi dei piani. Restano uguali in ogni lingua: sono nomi di prodotto. */
    names: {
      /** Piano gratuito. */
      free: 'Free',
      /** Piano base. */
      base: 'Base',
      /** Piano premium. */
      premium: 'Premium',
    },
    /** Che cosa dà ogni piano, nella scelta. */
    summaries: {
      /** Riassunto del free. */
      free: '1 set, 1 progetto, nodo condiviso',
      /** Riassunto del base. */
      base: '1 set, fino a 3 progetti, nodo personale',
      /** Riassunto del premium. */
      premium: 'Fino a 3 set, fino a 10 progetti per set, nodo personale più grande',
    },
    /** Tag accanto al piano di un amministratore della piattaforma. */
    unrestricted: 'Senza limiti',
    /** Pulsante della riga. */
    change: 'Cambia piano',
    /** Nome accessibile del pulsante, e titolo della finestra. */
    changeNamed: 'Cambia piano di {{name}}',
    /** Etichetta della scelta del piano. */
    field: 'Piano',
    /** Etichetta del motivo. */
    reason: 'Motivo',
    /** Aiuto sotto il motivo. */
    reasonHelp: 'Resta nello storico dei cambi. Al massimo 500 caratteri.',
    /** Che cosa succede con un piano più piccolo (D29). */
    downgradeNote:
      'Con un piano più piccolo set e progetti esistenti restano. Si blocca solo la creazione di quelli oltre il limite.',
    /** Avviso per un amministratore della piattaforma. */
    adminNote: 'Gli amministratori della piattaforma non hanno limiti, qualunque sia il piano.',
    /** Esito riuscito. */
    changed: 'Piano aggiornato.',
    /** Cambio non riuscito. */
    changeFailed: 'Cambio del piano non riuscito. Riprova.',
    /** Lettura del piano non riuscita. */
    loadFailed: 'Caricamento del piano non riuscito. Riprova.',
    /** Titolo dello storico. */
    history: 'Ultimi cambi',
    /** Storico vuoto. */
    historyEmpty: 'Nessun cambio registrato.',
    /** Una riga dello storico. */
    historyEntry: '{{date}}: da {{from}} a {{to}}',
    /** Regalo attivo (ADR 0061), nella finestra del piano e nella pagina Abbonamento. */
    grantedUntil: '{{plan}} regalato fino al {{date}}',
  },

  /**
   * Accesso come un altro utente (impersonazione, ADR 0009). Nell'interfaccia
   * la parola «impersonazione» non compare: si dice «accedi come».
   */
  impersonate: {
    /** Pulsante della riga. I puntini dicono che si apre una conferma (deroga dichiarata). */
    action: 'Accedi come…',
    /** Nome accessibile del pulsante, che nomina la riga. */
    actionNamed: 'Accedi come {{name}}',
    /** Titolo della finestra di conferma. */
    title: 'Accedi come {{name}}',
    /** Avviso di sicurezza in cima alla finestra. */
    warning:
      'Finché resti nel suo account agisci a suo nome, con i suoi permessi. Avvio e ritorno restano registrati con il tuo nome e la motivazione.',
    /** Operazioni chiuse durante l'accesso come un altro utente. */
    limits:
      'Nel suo account non puoi cambiare password e verifica in due passaggi, scrivere credenziali, eliminare progetti o amministrare gli utenti.',
    /** Etichetta della motivazione. */
    reason: 'Motivazione',
    /** Aiuto sotto la motivazione. */
    reasonHelp: 'Da 10 a 500 caratteri.',
    /** Motivazione mancante o troppo corta. */
    reasonTooShort: 'Scrivi una motivazione di almeno 10 caratteri.',
    /** Etichetta del codice. */
    code: 'Codice di verifica',
    /** Aiuto sotto il codice. */
    codeHelp: "Il codice a sei cifre che mostra ora la tua app di autenticazione.",
    /** Codice mancante o non di sei cifre. */
    codeInvalidFormat: 'Inserisci le sei cifre del codice.',
    /** Etichetta della durata. */
    duration: 'Durata',
    /** Una durata in minuti. */
    minutes_one: '{{count, number}} minuto',
    /** Una durata in minuti. */
    minutes_other: '{{count, number}} minuti',
    /** Pulsante che conferma. */
    confirm: 'Accedi',
    /** Codice rifiutato dal gateway. */
    invalidCode: "Codice non valido. Usa quello che mostra ora l'app.",
    /** Utente che non si può impersonare. */
    targetRefused: 'Non puoi accedere come questo utente.',
  },
};

/** Forma dei testi degli utenti, usata per tipare le altre lingue. */
export type Users = typeof users;
